import { SyntheticEvent } from 'react';
import { VerticalTabContainer } from 'components/tab/Tab.styles';
import { Col, Nav, NavItem, Row, TabContainer } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { normalizeKey } from 'utils/localeUtils';
import useIsMobileScreen from 'hooks/useIsMobileScreen';
import { Breakpoint, breakpoints } from 'styles/mediaQuery';
import useSidePanel from 'components/sidePanel/useSidePanel';
import {
  IntegrationsTab,
  integrationsTabSidePanelId,
} from './IntegrationsTabs.constants';
import useIntegrationsActiveTab from './useIntegrationsActiveTab';
import IntegrationsTabsHeader from './IntegrationsTabsHeader';
import { IntegrationsTabsContainer } from './IntegrationTabs.styles';

export const IntegrationsDefaultRoute = () => {
  return <Navigate to={IntegrationsTab.featured} replace />;
};

const IntegrationsTabs = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const activeTab = useIntegrationsActiveTab();
  const isMobile = useIsMobileScreen(breakpoints[Breakpoint.XS]);
  const { closeSidePanel } = useSidePanel({
    sidePanelId: integrationsTabSidePanelId,
  });

  const sortedTabsToRender = Object.values(IntegrationsTab)
    .map((tab) => ({
      path: tab,
      label: t(normalizeKey(`pageIntegrations.tabNames.${tab}`)) as string,
    }))
    .sort((a, b) => {
      // All tab always first
      if (a.path === IntegrationsTab.all) return -1;
      if (b.path === IntegrationsTab.all) return 1;

      // Featured tab always second
      if (a.path === IntegrationsTab.featured) return -1;
      if (b.path === IntegrationsTab.featured) return 1;

      // Others tabs are sorted alphabetically by label
      return a.label.localeCompare(b.label, language);
    });

  const onSelect = (event: SyntheticEvent<TabContainer, Event>) => {
    const tabName = event as unknown as string; // should be fixed by BS5

    navigate(tabName);
  };

  const integrationsTabs = (
    <Nav stacked>
      {sortedTabsToRender.map(({ path, label }) => (
        <NavItem
          data-testid={`integrations-tab-${path}`}
          eventKey={path}
          key={path}
          onClick={closeSidePanel}
        >
          {label}
        </NavItem>
      ))}
    </Nav>
  );

  return (
    <IntegrationsTabsContainer>
      <VerticalTabContainer activeKey={activeTab} onSelect={onSelect}>
        <Row>
          <Col sm={2}>{!isMobile && integrationsTabs}</Col>
          <Col sm={10}>
            <IntegrationsTabsHeader sidePanelContent={integrationsTabs} />
            <Outlet />
          </Col>
        </Row>
      </VerticalTabContainer>
    </IntegrationsTabsContainer>
  );
};

export default IntegrationsTabs;
